/**
 * The words for an address the one rule refuses (`src/lib/email-rule.ts`,
 * plan "Saubere Eingaben" 2026-10-05) — one place that turns a reason into an
 * `errors.*` line, read by the E-Mail cell (red line, read-mode warning) and
 * by `translateBackendError` for the server's `emailInvalid` + `detail`.
 *
 * Three lines may name the offending character; their copy is an ICU
 * `select` on `codePoint`: the `other` branch quotes `{char}` and
 * `{codePoint}`, the `none` branch (`EMAIL_RULE_NO_OFFENDER`) is for a
 * verdict without a character — the server sends only the reason, and a
 * domain without a dot has no single character to blame.
 */
import type { useTranslations } from "next-intl";
import {
  EMAIL_RULE_REASONS,
  emailRuleOffender,
  emailRuleReason,
  type EmailRuleReason,
} from "@/lib/email-rule";

type Translator = ReturnType<typeof useTranslations>;

/** `errors.*` key per reason. `shape` keeps the generic line. */
export const EMAIL_RULE_REASON_KEYS: Readonly<Record<EmailRuleReason, string>> = {
  invisible: "emailInvalidInvisible",
  whitespace: "emailInvalidWhitespace",
  shape: "emailInvalid",
  "local-non-ascii": "emailInvalidLocalNonAscii",
  "local-too-long": "emailInvalidLocalTooLong",
  domain: "emailInvalidDomain",
  "too-long": "emailInvalidTooLong",
};

/** The `select` value that asks a line for its no-character branch. */
export const EMAIL_RULE_NO_OFFENDER = "none";

export function isEmailRuleReason(value: string): value is EmailRuleReason {
  return (EMAIL_RULE_REASONS as readonly string[]).includes(value);
}

/**
 * A server `detail` for `emailInvalid`, taken apart. The reason arrives bare
 * (`invisible`) or in the constraint's `emailInvalid:<reason>` form; for an
 * element of a LIST (the create door sends a list, also for one guest)
 * main-backend's `ConstraintViolationExceptionMapper` appends the 0-based
 * position, `;index=<n>`. Null for anything that is not a reason this build
 * knows — the caller then says the generic line.
 */
export function parseEmailRuleDetail(
  detail: string,
): { reason: EmailRuleReason; index: number | null } | null {
  let rest = detail.trim();
  if (rest.startsWith("emailInvalid:")) rest = rest.slice("emailInvalid:".length);
  let index: number | null = null;
  const at = rest.indexOf(";");
  if (at >= 0) {
    const m = /^;index=(\d{1,9})$/.exec(rest.slice(at));
    if (!m) return null;
    index = Number(m[1]);
    rest = rest.slice(0, at);
  }
  return isEmailRuleReason(rest) ? { reason: rest, index } : null;
}

/** The reason inside a server `detail`, or null — see {@link parseEmailRuleDetail}. */
export function emailRuleReasonFromDetail(detail: string): EmailRuleReason | null {
  return parseEmailRuleDetail(detail)?.reason ?? null;
}

/**
 * The line for a reason, with the character when one is known. `t` is bound
 * to the `errors` namespace. A bundle that does not have the reason's line
 * answers with the generic one — never with a key path.
 */
export function emailRuleTextFor(
  t: Translator,
  reason: EmailRuleReason,
  offender: { char: string; codePoint: string } | null,
): string {
  const key = EMAIL_RULE_REASON_KEYS[reason];
  if (key === "emailInvalid") return t("emailInvalid");
  if (typeof t.has === "function" && !t.has(key)) return t("emailInvalid");
  return t(key, {
    char: offender?.char ?? "",
    codePoint: offender?.codePoint ?? EMAIL_RULE_NO_OFFENDER,
  });
}

/**
 * What the CELL says about `value` (already cleaned, or stored as is): null
 * when it is deliverable, else the reason's line — the character quoted when
 * one is to blame, the line's no-character branch otherwise ("max@example":
 * the domain line says it needs a dot and an ending).
 */
export function emailRuleErrorText(t: Translator, value: string): string | null {
  const reason = emailRuleReason(value);
  if (reason === null) return null;
  return emailRuleTextFor(t, reason, emailRuleOffender(value, reason));
}
